/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import { dateUtils } from '@opengeoweb/shared';
import type { TimelineEditing } from './WarningEventTimelineBlock';
import { WarningEventTimelineBlock } from './WarningEventTimelineBlock';
import type { PublicWarningStatus } from '../../../../store/warningsForm/types';
import {
  EDGE_PADDING,
  generateTicks,
  getTimelineX,
} from '../../../WarningsTimeline/warningsTimelineUtils';

export type { TimelineEditing };

const HEADER_HEIGHT = 32;
const AXIS_HEIGHT = 24;
const COMPACT_HEADER_HEIGHT = 14;
const COMPACT_AXIS_HEIGHT = 22;
const AXIS_TICK_HEIGHT = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface WarningEventTimelineProps {
  start: Date;
  end: Date;
  validFrom: string;
  validUntil: string;
  level: string;
  status?: PublicWarningStatus;
  now?: Date;
  isCompact?: boolean;
  isActive?: boolean;
  editing?: TimelineEditing;
}

const NOW_LINE_YELLOW = '#FAE21E';

interface AxisIntervals {
  tickHours: number;
  labelHours: number;
}

export const getAxisIntervals = (rangeDays: number): AxisIntervals => {
  if (rangeDays <= 1) {
    return { tickHours: 1, labelHours: 3 };
  }
  if (rangeDays <= 2) {
    return { tickHours: 3, labelHours: 6 };
  }
  return { tickHours: 6, labelHours: 12 };
};

export const formatDayHeader = (date: Date): string =>
  date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

export const WarningEventTimeline: React.FC<WarningEventTimelineProps> = ({
  start,
  end,
  validFrom,
  validUntil,
  level,
  status,
  now: nowProp,
  isCompact = false,
  isActive = false,
  editing,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(0);
  const headerHeight = isCompact ? COMPACT_HEADER_HEIGHT : HEADER_HEIGHT;
  const axisHeight = isCompact ? COMPACT_AXIS_HEIGHT : AXIS_HEIGHT;
  // Re-read on every render (not memoized) so the current-time marker and the
  // past/future split stay current for the lifetime of the form.
  const now = nowProp ?? new Date();

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const next = Math.round(entry.contentRect.width);
        setWidth((current) => (current === next ? current : next));
      }
    });
    resizeObserver.observe(node);
    return (): void => resizeObserver.disconnect();
  }, []);

  const drawableWidth = Math.max(1, width - 2 * EDGE_PADDING);
  const ticks = React.useMemo(() => generateTicks(start, end), [start, end]);
  const dayTicks = ticks.filter((tick) => tick.type === 'day');

  const rangeDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
  const { tickHours, labelHours } = getAxisIntervals(rangeDays);

  const showNow =
    now.getTime() >= start.getTime() && now.getTime() <= end.getTime();
  const nowX = getTimelineX(now, start, end, drawableWidth);

  // Day headers ("Today / +N") are labelled relative to the actual current day,
  // so a range that starts later than today (e.g. an early warning) still reads
  // "+2" on its first day rather than "Today".
  const dayZero = dateUtils.startOfDayUTC(now);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: 'geowebColors.background.surface',
        overflow: 'hidden',
      }}
    >
      {dayTicks.map((tick) => (
        <Box
          data-testid="timeline-day-line"
          key={`line-${tick.time.toISOString()}`}
          sx={{
            position: 'absolute',
            left: getTimelineX(tick.time, start, end, drawableWidth),
            top: 0,
            bottom: 0,
            borderLeft: '1px solid',
            borderColor: 'geowebColors.cards.cardContainerBorder',
          }}
        />
      ))}

      {showNow ? (
        <Box
          data-testid="timeline-now-line"
          sx={{
            position: 'absolute',
            left: nowX,
            top: 0,
            bottom: axisHeight - AXIS_TICK_HEIGHT,
            width: '3px',
            background: (theme) =>
              `linear-gradient(to right, ${NOW_LINE_YELLOW} 0 33.333%, ${theme.palette.geowebColors.functional.error} 33.333% 66.667%, ${NOW_LINE_YELLOW} 66.667% 100%)`,
            zIndex: 2,
          }}
        />
      ) : null}

      {dayTicks
        .filter((tick) => tick.time.getTime() < end.getTime())
        .map((tick) => {
          const dayOffset = Math.round(
            (tick.time.getTime() - dayZero.getTime()) / MS_PER_DAY,
          );
          let offsetLabel = 'Today';
          if (dayOffset > 0) {
            offsetLabel = `+${dayOffset}`;
          } else if (dayOffset < 0) {
            offsetLabel = `${dayOffset}`;
          }
          return (
            <Box
              data-testid="timeline-day-header"
              key={`header-${tick.time.toISOString()}`}
              sx={{
                position: 'absolute',
                left: getTimelineX(tick.time, start, end, drawableWidth),
                top: isCompact ? 0 : 2,
                paddingLeft: 0.5,
                lineHeight: isCompact ? 1 : 1.15,
                whiteSpace: 'nowrap',
                zIndex: 3,
              }}
            >
              <Box
                sx={{
                  fontSize: isCompact ? 11 : 12,
                  fontWeight: 'bold',
                  color: 'geowebColors.typographyAndIcons.text',
                }}
              >
                {offsetLabel}
              </Box>
              {!isCompact ? (
                <Box
                  sx={{
                    fontSize: 11,
                    color: 'geowebColors.typographyAndIcons.text',
                  }}
                >
                  {formatDayHeader(tick.time)}
                </Box>
              ) : null}
            </Box>
          );
        })}

      {/* Warning block area */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: headerHeight,
          bottom: axisHeight,
        }}
      >
        <WarningEventTimelineBlock
          drawableWidth={drawableWidth}
          editing={editing}
          end={end}
          isActive={isActive}
          isCompact={isCompact}
          level={level}
          now={now}
          start={start}
          status={status}
          validFrom={validFrom}
          validUntil={validUntil}
        />
      </Box>

      {/* Bottom axis */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: axisHeight,
        }}
      >
        {ticks.map((tick) => {
          const hour = tick.time.getUTCHours();
          const showTick = tick.type !== 'day' && hour % tickHours === 0;
          const showLabel = hour % labelHours === 0;
          if (!showTick && !showLabel) {
            return null;
          }
          const x = getTimelineX(tick.time, start, end, drawableWidth);
          return (
            <React.Fragment key={`tick-${tick.time.toISOString()}`}>
              {showTick ? (
                <Box
                  data-testid="timeline-hour-tick"
                  sx={{
                    position: 'absolute',
                    left: x,
                    top: 0,
                    height: showLabel ? AXIS_TICK_HEIGHT : 4,
                    borderLeft: '1px solid',
                    borderColor: 'geowebColors.typographyAndIcons.inactiveText',
                  }}
                />
              ) : null}
              {showLabel ? (
                <Box
                  data-testid="timeline-axis-label"
                  sx={{
                    position: 'absolute',
                    left: x,
                    top: 9,
                    transform: 'translateX(-50%)',
                    fontSize: 11,
                    color: 'geowebColors.typographyAndIcons.inactiveText',
                  }}
                >
                  {`${String(tick.time.getUTCHours()).padStart(2, '0')}:00`}
                </Box>
              ) : null}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};
